import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ButtonSp from '../../components/ButtonSp';

import InputTextSp from '../../components/InputTextSp';

import LabelSp from '../../components/LabelSp';
import { showMessage } from '../../components/MessageDialog';

import OrcamentoService from '../../services/OrcamentoService';
import MunicipioService from '../../services/MunicipioService';

import { errorHandle, formatDate, isDate, strToDate } from '../../util/functions';
import { OrcamentoCabecalhoModel } from '../../util/Models';
import { StateScreen } from '../constants';
import DropdownSp from '../../components/DropdownSp';
import InputMaskSp from '../../components/InputMaskSp';
import InputTextareaSp from '../../components/InputTextareaSp';

import { cfgPtBr, listaUfs } from '../../config/Constantes';
import DropDownLazy from '../../components/DropdownLazy';

import AuthService from '../../services/AuthService';
import RevendaService from '../../services/RevendaService';

import DispositivoService from '../../services/DispositivoService';
import SelectButtons from '../../components/SelectButtons';
import ContratoOrcamento from '../../components/ContratoOrcamento/contrato';
import CalendarSp from '../../components/CalendarSp';
import SistemaService from '../../services/SistemaService';
import ClienteService from '../../services/ClienteService';

function OrcamentoCrud(props) {
  OrcamentoCrud.propTypes = {
    stateScreen: PropTypes.string.isRequired,
    idSelected: PropTypes.number.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  OrcamentoCrud.defaultProps = {
    messageConfirmation: 'Confirma os dados',
    showConfirmation: false,
  };

  const { stateScreen, idSelected, onClose } = props;

  const revendaUsuario = AuthService.getUsuario().revenda
    ? {
        label: AuthService.getUsuario().revenda.nome,
        value: AuthService.getUsuario().revenda.id,
      }
    : null;

  // states
  const [orcamento, setOrcamento] = useState(new OrcamentoCabecalhoModel());
  const [municipioSelecionado, setMunicipioSelecionado] = useState();
  const [revendaSelecionada, setRevendaSelecionada] = useState();

  const [paginaSelecionada, setPaginaSelecionada] = useState(0);

  const [cpf, setCpf] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [ufSelecionada, setUfSelecionada] = useState('MG');
  const [errorLoadRecord, setErrorLoadRecord] = useState(false);
  const [dispositivosUsados, setDispositivosUsados] = useState('');
  const [valorOriginal, setValorOriginal] = useState(0);

  // useCallbacks

  const loadMunicipio = useCallback(async (_nome, uf) => {
    if (_nome !== undefined) {
      const r = await MunicipioService.findAll({ nome: _nome, uf, limit: 50 });

      const retorno = r.items.map(e => {
        return {
          label: e.nome,
          value: e.id,
        };
      });
      return retorno;
    }
    return [];
  }, []);

  const loadRecord = useCallback(async _id => {
    try {
      const retorno = await OrcamentoService.findById(_id);
      setValorOriginal(retorno.percentualSalario);

      const municipio = retorno.municipio
        ? {
            label: retorno.municipio.nome,
            value: retorno.municipio.id,
          }
        : null;

      const revenda = retorno.revenda
        ? {
            label: retorno.revenda.nome,
            value: retorno.revenda.id,
          }
        : null;

      if (isDate(retorno.dataValidade)) {
        retorno.dataValidade = strToDate(retorno.dataValidade);
      }

      if (isDate(retorno.dataLancamento)) {
        retorno.dataLancamento = strToDate(retorno.dataLancamento);
      }

      if (isDate(retorno.dataFimContrato)) {
        retorno.dataFimContrato = strToDate(retorno.dataFimContrato);
      }

      if (retorno.numeroContrato) {
        const chaveDispositivos = await DispositivoService.findAll({
          numeroContrato: retorno.numeroContrato,
        });

        setDispositivosUsados(chaveDispositivos.items.length);
      }

      setRevendaSelecionada(revenda);
      setOrcamento(retorno);
      setMunicipioSelecionado(municipio);

      if (retorno.tipoPessoa === 'F') {
        setCpf(retorno.cpfCnpj);
        setCnpj('');
      } else {
        setCpf('');
        setCnpj(retorno.cpfCnpj);
      }

      setErrorLoadRecord(false);
    } catch (err) {
      setErrorLoadRecord(true);
      errorHandle(err);
    }
  }, []);

  const loadRevenda = useCallback(async _nome => {
    if (_nome !== undefined) {
      const r = await RevendaService.findAll({ nome: _nome, limit: 50 });

      const retorno = r.items.map(e => {
        return {
          label: e.nome,
          value: e.id,
        };
      });
      return retorno;
    }
    return [];
  }, []);

  // funcoes
  function viewMode() {
    return stateScreen === StateScreen.stView || errorLoadRecord;
  }

  function bloqueiaRevenda() {
    return AuthService.getUsuario().idRevenda && !AuthService.isPerfilGerente();
  }

  function handleBack() {
    if (stateScreen === StateScreen.stView) {
      onClose();
    } else {
      showMessage('Confirmação', 'Abandonar mudanças?', idx => {
        if (idx === 1) {
          onClose();
        }
      });
    }
  }

  function validateRecord() {
    if (!orcamento.dataValidade) {
      toast.error('Informe a data de validade');
      return false;
    }

    if (orcamento.dataLancamento.getTime() > orcamento.dataValidade.getTime()) {
      toast.warn(
        `Data de validade deve ser inferior a data de lançamento: ${formatDate(
          orcamento.dataValidade,
          'dd/MM/yyyy'
        )}`
      );
      return;
    }

    if (orcamento.tipoPessoa === 'F' && !cpf) {
      toast.error('Informe o CPF');
      return false;
    }

    if (orcamento.tipoPessoa === 'J' && !cnpj) {
      toast.error('Informe o CNPJ');
      return false;
    }

    if (!orcamento.idRevenda) {
      toast.error('Informe a Revenda');
      return false;
    }
    if (!orcamento.nome) {
      toast.error('Informe o nome');
      return false;
    }

    if (!orcamento.idMunicipio) {
      toast.error('Informe o município');
      return false;
    }

    if (!orcamento.numeroDispositivos) {
      toast.error('Informe número de dispositívos');
      setPaginaSelecionada(1);
      return false;
    }

    if (!orcamento.percentualSalarioCalculado) {
      toast.error('É necessário calcular o % salário do contrato');
      setPaginaSelecionada(1);
      return false;
    }

    if (!orcamento.percentualSalario) {
      toast.error('É necessário informar o % do salário');
      setPaginaSelecionada(1);
      return false;
    }

    if (
      valorOriginal !== orcamento.percentualSalario &&
      orcamento.percentualSalarioCalculado > orcamento.percentualSalario
    ) {
      if (
        !AuthService.isPerfilGerente() &&
        1 - orcamento.percentualSalario / orcamento.percentualSalarioCalculado > 0.05
      ) {
        toast.error('Desconto máximo de 5% ultrapassado.');
        setPaginaSelecionada(1);
        return false;
      }
    }

    if (orcamento.inativo && !orcamento.dataFimContrato) {
      toast.error('Informe a data do final do contrato');
      setPaginaSelecionada(1);
      return false;
    }

    return true;
  }

  function handleSave() {
    if (!validateRecord()) {
      return;
    }

    showMessage('Confirmação', 'Confirma os dados?', idx => {
      if (idx === 1) {
        if (stateScreen === StateScreen.stView) {
          onClose();
        } else {
          salvarRecord();
        }
      }
    });
  }

  async function salvarRecord() {
    try {
      let retorno;
      if (orcamento.tipoPessoa === 'F') {
        orcamento.cpfCnpj = cpf;
      } else {
        orcamento.cpfCnpj = cnpj;
      }
      orcamento.idUsuario = AuthService.getUsuario().id;
      if (stateScreen === StateScreen.stInsert) {
        retorno = await OrcamentoService.insert(orcamento);
      } else {
        retorno = await OrcamentoService.update(orcamento);
      }
      toast.success('Registro salvo com sucesso.');
      onClose(retorno);
    } catch (err) {
      errorHandle(err);
    }
  }

  // useEffects
  useEffect(() => {
    if (stateScreen === StateScreen.stUpdate || stateScreen === StateScreen.stView) {
      loadRecord(idSelected);
    } else if (stateScreen === StateScreen.stInsert) {
      const carregaOrcamento = async () => {
        const novo = new OrcamentoCabecalhoModel();
        novo.valorSalarioBase = AuthService.getConfiguracao()?.valorSalarioVigente;
        if (AuthService.getUsuario().idRevenda) {
          novo.idRevenda = AuthService.getUsuario().idRevenda;
          novo.revenda = AuthService.getUsuario().revenda;
        }

        const sistemaErp = await SistemaService.findById(1);
        if (sistemaErp) {
          novo.sistema = sistemaErp;
          novo.idSistema = sistemaErp.id;
        }

        setCpf('');
        setCnpj('');

        setOrcamento(novo);
        setRevendaSelecionada(revendaUsuario);
      };
      carregaOrcamento();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRecord, idSelected, stateScreen]);

  const renderPaginaSelecionada = () => {
    if (paginaSelecionada === 0) {
      return renderCadastro();
    }
    if (paginaSelecionada === 1) {
      return renderContrato();
    }
  };
  const buttons = ['Cadastro', 'Contrato'];

  const consultaCnpj = async () => {
    try {
      const cnpjConsulta = cnpj.replace(/[^\d]/g, '');
      if (!cnpjConsulta) {
        toast.error('CNPJ Inválido');
        return;
      }
      const clienteConsulta = await ClienteService.consultaCNPJ(cnpjConsulta);

      if (clienteConsulta.municipio) {
        setMunicipioSelecionado({
          value: clienteConsulta.municipio.id,
          label: clienteConsulta.municipio.nome,
        });
      }
      if (clienteConsulta) {
        setOrcamento({
          ...orcamento,
          nome: clienteConsulta.nome,
          nomeFantasia: clienteConsulta.nomeFantasia,
          fone: clienteConsulta.telefone,
          municipio: clienteConsulta.municipio,
          idMunicipio: clienteConsulta.municipio?.id,
          email: clienteConsulta.email,
        });
      }
    } catch (err) {
      errorHandle(err);
    }
  };

  // render principal
  return (
    <>
      <div className="p-col-12 p-lg-12" style={{ padding: 0, margin: 0 }}>
        <SelectButtons
          widthButton={200}
          buttons={buttons}
          selectedIndex={paginaSelecionada}
          onClick={itemIndex => setPaginaSelecionada(itemIndex)}
        />
      </div>
      {renderPaginaSelecionada()}
    </>
  );

  function renderCadastro() {
    return (
      <>
        <>
          <div className="p-col-6 p-md-4 p-fluid">
            <LabelSp>Data</LabelSp>
            <CalendarSp
              showOnFocus={false}
              showIcon
              required
              dateFormat="dd/mm/yy"
              disabled
              locale={cfgPtBr}
              value={orcamento.dataLancamento}
              appendTo={document.body}
              onChange={e => {
                setOrcamento({
                  ...orcamento,
                  dataLancamento: e.target.value,
                });
              }}
            />
          </div>
          <div className="p-col-6 p-md-4 p-fluid">
            <LabelSp>Validade</LabelSp>
            <CalendarSp
              showOnFocus={false}
              showIcon
              required
              dateFormat="dd/mm/yy"
              disabled={viewMode()}
              locale={cfgPtBr}
              value={orcamento.dataValidade}
              appendTo={document.body}
              onChange={e => {
                setOrcamento({
                  ...orcamento,
                  dataValidade: e.target.value,
                });
              }}
            />
          </div>
          <div className="p-col-12 p-md-4 p-lg-4 p-fluid">
            <LabelSp>Revenda</LabelSp>
            <DropDownLazy
              autoLoad
              placeholder="Selecione"
              required
              value={revendaSelecionada}
              onChange={e => {
                setRevendaSelecionada(e);
                const revenda = e ? { id: e.value, nome: e.label } : null;
                setOrcamento({
                  ...orcamento,
                  revenda,
                  idRevenda: e?.value,
                });
              }}
              onFilter={async txtFilter => {
                const retorno = await loadRevenda(txtFilter);
                return retorno;
              }}
              disabled={viewMode() || bloqueiaRevenda()}
            />
          </div>

          <div className="p-col-12 p-md-6 p-lg-6 p-fluid" style={{ margin: 0, padding: 0 }}>
            <div className="p-grid" style={{ margin: 0, padding: 0 }}>
              <div className="p-col-5 p-fluid">
                <LabelSp>Tp. Pessoa</LabelSp>
                <DropdownSp
                  autoFocus
                  value={orcamento.tipoPessoa}
                  name="cmbTipoPessoa"
                  disabled={viewMode()}
                  options={[
                    { label: 'Física', value: 'F' },
                    { label: 'Jurídica', value: 'J' },
                  ]}
                  onChange={e => {
                    setOrcamento({ ...orcamento, tipoPessoa: e.target.value });
                  }}
                />
              </div>

              {orcamento.tipoPessoa === 'F' ? (
                <div className="p-col-7 p-fluid">
                  <LabelSp>CPF</LabelSp>
                  <InputMaskSp
                    required
                    name="txtCPF"
                    appAutofocus
                    mask="999.999.999-99"
                    value={cpf}
                    disabled={viewMode()}
                    onChange={e => {
                      setCpf(e.target.value);
                    }}
                  />
                </div>
              ) : (
                <div className="p-col-7 p-fluid" style={{ margin: 0, padding: 0 }}>
                  <div style={{ margin: 0, padding: 0, display: 'flex' }}>
                    <div style={{ margin: 0, padding: 3, flex: 1 }}>
                      <LabelSp>CNPJ</LabelSp>
                      <InputMaskSp
                        mask="99.999.999/9999-99"
                        required
                        value={cnpj}
                        disabled={viewMode()}
                        onChange={e => {
                          setCnpj(e.target.value);
                        }}
                      />
                    </div>
                    <div style={{ margin: 0, padding: 3, paddingRight: 0, paddingLeft: 0 }}>
                      <LabelSp style={{ display: 'block' }}>&#160;</LabelSp>
                      <ButtonSp
                        icon="pi pi-search"
                        title="Consulta CNPJ"
                        onClick={consultaCnpj}
                        disabled={viewMode()}
                        type="button"
                        className="p-button-secondary"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-col-12 p-md-6 p-lg-6 p-fluid">
            <LabelSp>Razão Social</LabelSp>
            <InputTextSp
              value={orcamento.nome}
              disabled={viewMode()}
              maxLength={100}
              required
              onChange={e => {
                setOrcamento({ ...orcamento, nome: e.target.value });
              }}
            />
          </div>

          <div className="p-col-3 p-md-2 p-lg-2 p-fluid">
            <LabelSp>UF</LabelSp>
            <DropdownSp
              options={listaUfs}
              disabled={viewMode()}
              value={ufSelecionada}
              onChange={e => {
                setUfSelecionada(e.value);
              }}
            />
          </div>

          <div className="p-col-9 p-md-5 p-lg-5 p-fluid">
            <LabelSp>Município</LabelSp>
            <DropDownLazy
              autoLoad
              placeholder="Selecione"
              required
              value={municipioSelecionado}
              onChange={e => {
                setMunicipioSelecionado(e);

                const municipio = e ? { id: e.value, nome: e.label } : null;

                setOrcamento({
                  ...orcamento,
                  idMunicipio: e?.value || undefined,
                  municipio,
                });
              }}
              onFilter={async txtFilter => {
                const retorno = await loadMunicipio(txtFilter, ufSelecionada);
                return retorno;
              }}
              disabled={viewMode()}
            />
          </div>

          <div className="p-col-12 p-md-5 p-lg-5 p-fluid">
            <LabelSp>e-mail</LabelSp>
            <InputTextSp
              name="txtEmail"
              value={orcamento.email}
              onChange={e => {
                setOrcamento({
                  ...orcamento,
                  email: e.target.value,
                });
              }}
              disabled={viewMode()}
            />
          </div>

          <div className="p-col-12 p-md-4 p-lg-4 p-fluid">
            <LabelSp>Nome Contato</LabelSp>
            <InputTextSp
              required
              value={orcamento.contato}
              onChange={e => {
                setOrcamento({
                  ...orcamento,
                  contato: e.target.value,
                });
              }}
              disabled={viewMode()}
            />
          </div>

          <div className="p-col-6 p-md-4 p-lg-4 p-fluid">
            <LabelSp>Telefone</LabelSp>
            <InputMaskSp
              mask="(99)9999-9999"
              value={orcamento.fone}
              onChange={e => {
                setOrcamento({
                  ...orcamento,
                  fone: e.target.value,
                });
              }}
              disabled={viewMode()}
            />
          </div>

          <div className="p-col-6 p-md-4 p-lg-4 p-fluid">
            <LabelSp>Celular</LabelSp>
            <InputMaskSp
              mask="(99)99999-9999"
              value={orcamento.celular}
              onChange={e => {
                setOrcamento({
                  ...orcamento,
                  celular: e.target.value,
                });
              }}
              disabled={viewMode()}
            />
          </div>

          <div className="p-col-12 p-md-12 p-lg-12 p-fluid">
            <LabelSp>Observação</LabelSp>
            <InputTextareaSp
              autoResize={false}
              value={orcamento.observacao}
              maxLength={2000}
              rows={4}
              resize={false}
              onChange={e => {
                setOrcamento({
                  ...orcamento,
                  observacao: e.target.value,
                });
              }}
              style={{ minHeight: 93 }}
              disabled={viewMode()}
            />
          </div>

          <div className="p-col-12 p-fluid">
            <hr style={{ marginTop: 3 }} />
          </div>

          {renderBotoes()}
        </>
      </>
    );
  }

  function renderBotoes() {
    return (
      <div className="p-col-12 p-lg-12" style={{ textAlign: 'start' }}>
        {!viewMode() ? (
          <ButtonSp
            className="p-button-success"
            icon="pi pi-save"
            label="Salvar"
            keyAccess="S"
            onClick={handleSave}
          />
        ) : null}
        <ButtonSp
          className="p-button-secondary"
          label="Voltar"
          keyAccess="V"
          icon="pi pi-chevron-circle-left"
          onClick={handleBack}
        />
      </div>
    );
  }

  function renderContrato() {
    return (
      <>
        <ContratoOrcamento
          orcamento
          contrato={{ ...orcamento }}
          permissaoAdminAlteracao={() => true}
          permissaoModulos={() => true}
          setContrato={setOrcamento}
          viewMode={viewMode}
          dispositivosUsados={dispositivosUsados}
        />

        {renderBotoes()}
      </>
    );
  }
}

export default OrcamentoCrud;
